/**
 * navItems — DsShell 내비게이션 단일 SoT (sidebar + bottom).
 *
 * 각 항목: { key, label, icon, path, showIn, group, min? }
 *   - icon: DsIcon 맵의 문자열 키 (src/ds/DsIcon.jsx)
 *   - showIn: ["sidebar"] / ["bottom"] / 둘 다
 *   - deeplink:true 는 라우트가 아니라 기존 대시보드 내부 탭으로의 딥링크.
 *   - embed:true + url 은 앱 내 임베드(본체=path 내부 라우트) + ↗ 화살표=url 새 탭 팝업.
 *   - group: 사이드바 구분선 묶음. 값이 바뀌는 지점에 얇은 선이 들어간다.
 *   - min: 최소 역할("owner"|"exec"|"manager"). 없으면 전원(직원 포함) 노출.
 *     (2026-07-24 PIN 대체: 정산=owner, SMBM=exec+, 설정=manager+. 계약/전자계약=전원.)
 *
 * 순서 규약(사장 확정 2026-07-26): **업무 흐름순**.
 *   영업(문의 접수) → 견적 → 계약 → 정산·머니 → AS(준공 후)·도구.
 *   이전엔 첫 단계(영업)가 마지막 단계(AS) 뒤에 있어 흐름이 뒤집혀 있었다.
 */
import { roleAtLeast, minRoleFor, canAccess } from "../auth/access";

export const NAV_ITEMS = [
  // ⓪ 앱 허브 — 기존 iG links 디자인 유지(사장 2026-07-27 "첫 페이지 유지"),
  //    home.igggstudio.com 분리 배포 + SSO 게이트. 새 탭으로 연다.
  { key: "launcher", label: "앱 허브", icon: "home", url: "https://home.igggstudio.com/", external: true, showIn: ["sidebar"], group: "home" },
  // 첫 화면 현황판(사장 2026-08-03) — "/" 는 대시보드. field 는 estimate 미보유라 자동 숨김.
  { key: "overview", label: "Dashboard", icon: "home", path: "/", showIn: ["sidebar"], feature: "estimate", group: "home" },
  // ① 수주 — 영업 파이프라인(문의→계약 단계 보드, 대표 전용)
  { key: "pipeline", label: "영업", icon: "tag", path: "/pipeline", showIn: ["sidebar"], feature: "pipeline", group: "sales" },
  // ② 견적 — 견적 만들기는 /create 로 이동("/"=현황판 교체, 메뉴·북마크 유지)
  { key: "create", label: "견적 만들기", icon: "plus", path: "/create", showIn: ["sidebar", "bottom"], feature: "estimate", group: "estimate" },
  { key: "dashboard", label: "목록 관리", icon: "database", path: "/dashboard", showIn: ["sidebar", "bottom"], feature: "estimate", group: "estimate" },
  // QCC — 현장팀 개방(사장 지시 2026-07-28). 견적 계열과 별도 키라 estimate 를 쓰지 않는다.
  { key: "qcc", label: "견적 체크", icon: "check", path: "/qcc", showIn: ["sidebar", "bottom"], feature: "qcc", group: "estimate" },
  { key: "franchise", label: "프랜차이즈", icon: "tag", path: "/franchise", showIn: ["sidebar"], feature: "estimate", group: "estimate" },
  // ③ 계약 — 계약서 → 전자계약(작성 후 발송) 순서 고정
  { key: "contract", label: "계약서", icon: "pen", path: "/contract", showIn: ["sidebar"], feature: "contract", group: "contract" },
  { key: "esign", label: "전자계약", icon: "shield", path: "/esign", showIn: ["sidebar"], feature: "esign", group: "contract" },
  // ④ 돈 — 정산(사장 전용) → SMBM(머니앱). ?tenant= 로 작업 공간 고정:
  //    main=이견, b2=사장 보조 사업자(SMBM lite). 본체=앱 내 임베드, ↗=새 탭 팝업.
  { key: "settlement", label: "정산", icon: "chart", path: "/settlement", showIn: ["sidebar"], feature: "settlement", group: "money" },
  { key: "smbm", label: "SMBM", icon: "chart", embed: true, url: "https://money.igggstudio.com/?tenant=main", path: "/smbm", showIn: ["sidebar"], feature: "smbm", group: "money" },
  { key: "smbm_lite", label: "SMBM lite", icon: "chart", embed: true, url: "https://money.igggstudio.com/?tenant=b2", path: "/smbm-lite", showIn: ["sidebar"], feature: "smbm_lite", group: "money" },
  // ⑤ 준공 후 · 도구 — AS 접수 관리 + 상시 도구
  { key: "as_ops", label: "AS유지보수", icon: "shield", path: "/as-admin", showIn: ["sidebar"], feature: "as_ops", group: "after" },
  // 기타문서 — 현장팀 개방(사장 지시 2026-07-28). 견적 계열과 별도 키.
  { key: "docs", label: "기타문서", icon: "doc", path: "/docs", showIn: ["sidebar"], feature: "docs", group: "after" },
  { key: "clipboard", label: "클립보드", icon: "copy", path: "/clipboard", showIn: ["sidebar"], feature: "estimate", group: "after" },
  { key: "trash", label: "휴지통", icon: "trash", path: "/trash", showIn: ["sidebar"], feature: "estimate", group: "after" },
  // 설정 자체는 전원(테마는 직원도). 내부 항목은 SettingsPage 에서 역할별 게이트
  // (단가DB·미등록단가=관리자+, 사업자정보·전자계약서명·직원관리=사장).
  { key: "settings", label: "설정", icon: "settings", path: "/settings", showIn: ["sidebar"], group: "after" },
];

export const SIDEBAR_ITEMS = NAV_ITEMS.filter((i) => i.showIn.includes("sidebar"));

/** 역할+접근config 로 걸러진 사이드바 항목. feature 있으면 config 의 최소역할, 없으면 전원. */
export function sidebarItemsFor(role, access, deny) {
  return SIDEBAR_ITEMS.filter((i) => {
    if (i.feature) return canAccess(role, i.feature, access, deny);   // 제외 포함(#56)
    return !i.min || roleAtLeast(role, i.min);
  });
}
